import { isAuthenticated, clear } from './auth.js';
import { setUnauthorizedHandler } from './api.js';
import { stateError, stateLoading } from './components/state-card.js';

const routes = new Map();   // path => { mount(target, params), unmount?(), public?: bool }
let currentUnmount = null;
let outletEl = null;
// Whether a render has already completed in this document. Gates the
// heading-focus jump in `afterRender` — see the comment there.
let firstRenderDone = false;

// Counts navigations. `render()` bumps it on entry, so anything that awaits
// during a render can compare against `navSeq` afterwards and find out whether
// it is still the current navigation.
//
// This exists because `mount` is asynchronous, which makes "the user navigated
// away while the page was still loading" a real window rather than a
// theoretical one — a chunk fetch or an API call is hundreds of milliseconds on
// a phone network. Without the check, an abandoned route mounted its page into
// the outlet that already held the next one, and its cleanup function displaced
// the visible page's, so the next navigation tore down a page nobody had seen
// and left the visible one's intervals running.
let navSeq = 0;

/** The current navigation's sequence number, for code that awaits mid-render. */
function currentNav() { return navSeq; }

// sessionStorage key read by the login page to explain why the user was
// signed out (session expired / revoked / …).
export const AUTH_NOTICE_STORAGE_KEY = 'lardr.authNotice';

const REASON_TO_I18N_KEY = {
  invalid_token:   'auth.toast.invalidToken',
  session_revoked: 'auth.toast.sessionRevoked',
  session_expired: 'auth.toast.sessionExpired',
  user_not_found:  'auth.toast.userNotFound',
};

export function setOutlet(el) { outletEl = el; }

export function register(path, route) {
  routes.set(path, route);
}

// How long a chunk fetch may take before the outlet shows a skeleton. A cached
// chunk resolves in single-digit milliseconds, and rendering a skeleton for that
// long reads as a flicker — worse than the blank frame it replaces. Only a real
// network fetch is slow enough to be worth acknowledging.
const CHUNK_SPINNER_DELAY_MS = 150;

/**
 * Register a page whose module is fetched on first navigation to it.
 *
 * This is what makes route-level code splitting possible: because the page is
 * behind `import()` rather than a static import in main.js, esbuild can emit it
 * as its own chunk and a cold start stops downloading all twenty screens.
 *
 * Two failure modes come with that and are handled here rather than left to the
 * twenty call sites:
 *
 *   slow  — a chunk on a real network takes long enough that the cleared outlet
 *           would sit visibly blank; after CHUNK_SPINNER_DELAY_MS it shows the
 *           standard loading state instead.
 *   dead  — the chunk cannot be fetched at all. This is not hypothetical: the
 *           service worker only precaches chunks after activation, so a route
 *           never visited online and then opened offline has nothing to load.
 *           A rejected import inside `mount` would otherwise surface as a
 *           permanently empty page with an error only in the console. It renders
 *           the shared error card with a Retry.
 *
 * Retry RELOADS THE DOCUMENT rather than re-running the route, and that is not a
 * shortcut. A failed dynamic import is memoised in the module map: calling
 * `import('./pages/timers.js')` again resolves to the same rejected record and
 * issues no network request at all — verified in Chromium, where a re-render
 * retry produced zero new requests and the error card simply stayed. Nor can the
 * specifier be cache-busted, because esbuild rewrites it to a hashed chunk name
 * at build time and a template literal would not be statically analysable. A new
 * document is the only thing that gets a clean module map. The hash already names
 * the route, so the reload returns to the page the user asked for.
 *
 * @param {() => Promise<object>} loader     e.g. () => import('./pages/units.js')
 * @param {(mod: object) => {mount: Function}} [pick]  selects the route export
 * @param {object} [extra]                   merged in (e.g. `{ public: true }`)
 * @returns {{mount: Function, preload: () => Promise<void>}}
 */
export function lazy(loader, pick = (m) => m.route, extra = {}) {
  return {
    ...extra,

    // Warm the module registry and the SW cache without rendering. Never
    // rejects: a prefetch that fails must not become an unhandled rejection,
    // the navigation itself will surface the problem if it recurs.
    preload: () => loader().then(() => {}, () => {}),

    async mount(target, params) {
      // Which navigation this mount belongs to. `render()` has already bumped
      // the counter, so this is our own number; if it has moved on by the time
      // the chunk arrives, the user left and this page must not draw anything.
      const nav = currentNav();

      let skeleton = null;
      const timer = setTimeout(() => {
        skeleton = stateLoading();
        target.append(skeleton);
      }, CHUNK_SPINNER_DELAY_MS);

      let mod;
      try {
        mod = await loader();
      } catch {
        clearTimeout(timer);
        if (skeleton) skeleton.remove();
        // No error card for a route the user has already left — it would land
        // on top of whatever page they went to.
        if (nav !== currentNav()) return undefined;
        target.append(stateError({
          messageKey: 'errors.network',
          onRetry: () => { if (typeof location?.reload === 'function') location.reload(); },
        }));
        return undefined;
      }

      clearTimeout(timer);
      if (skeleton) skeleton.remove();
      // The chunk arrived after the user moved on. Mounting now would append a
      // second page into the outlet that already holds the current one.
      if (nav !== currentNav()) return undefined;
      return pick(mod).mount(target, params);
    },
  };
}

export function go(path) {
  if (location.hash === '#' + path) {
    render();
  } else {
    location.hash = '#' + path;
  }
}

function parseHash() {
  const raw = location.hash || '#/';
  return raw.startsWith('#') ? raw.slice(1) : raw;
}

/**
 * Resolve a hash path to a registered route, supporting dynamic `:param`
 * segments. Exact matches win over pattern matches.
 *
 * @returns {{route: object, params: Record<string, string>}|null}
 */
function resolve(path) {
  // 1) Exact match
  const exact = routes.get(path);
  if (exact) return { route: exact, params: {} };

  // 2) Pattern match — split both sides on '/' and require equal segment counts.
  //    A pattern segment starting with ':' captures any non-empty value.
  const pathParts = path.split('/');
  for (const [pattern, route] of routes) {
    if (!pattern.includes(':')) continue;
    const patternParts = pattern.split('/');
    if (patternParts.length !== pathParts.length) continue;
    const params = {};
    let ok = true;
    for (let i = 0; i < patternParts.length; i++) {
      const pp = patternParts[i];
      const ap = pathParts[i];
      if (pp.startsWith(':')) {
        if (ap === '') { ok = false; break; }
        params[pp.slice(1)] = decodeURIComponent(ap);
      } else if (pp !== ap) {
        ok = false;
        break;
      }
    }
    if (ok) return { route, params };
  }
  return null;
}

async function render() {
  // Claim this navigation. Everything below that awaits compares against
  // `navSeq` afterwards to find out whether it is still the current one.
  const nav = ++navSeq;
  const path = parseHash();
  let resolved = resolve(path);
  if (!resolved) {
    // Unknown route — redirect to the default route so the URL reflects
    // what is actually rendered (stale bookmarks, typo'd deep links).
    if (path !== '/' && routes.has('/')) return go('/');
    if (!routes.has('/')) return;
    resolved = { route: routes.get('/'), params: {} };
  }
  const { route, params } = resolved;

  if (!route.public && !isAuthenticated()) {
    if (path !== '/login') return go('/login');
  }

  if (currentUnmount) {
    try { currentUnmount(); } catch (e) { console.warn(e); }
    currentUnmount = null;
  }
  if (!outletEl) return;
  // Clear outlet safely without innerHTML
  while (outletEl.firstChild) outletEl.removeChild(outletEl.firstChild);
  // Page-enter fade. Opacity only — a transform here would turn the outlet
  // into a containing block and break position:fixed page children. Removing
  // and re-adding the class restarts the animation on every navigation.
  outletEl.classList.remove('anim-fade-in');
  void outletEl.offsetWidth;
  outletEl.classList.add('anim-fade-in');
  const result = await route.mount(outletEl, params);

  // A newer navigation started while this page was mounting. Adopting its
  // cleanup function now would displace the visible page's, so the next
  // navigation would tear down a page nobody saw and leave the visible one's
  // intervals and listeners running. And `afterRender` would scroll and move
  // focus on behalf of a route that is no longer on screen.
  if (nav !== navSeq) return;

  if (typeof result === 'function') currentUnmount = result;

  afterRender(path);
}

/**
 * Route-change hygiene, run after every completed render.
 *
 * None of this existed: a scrolled inventory list carried its offset onto the
 * next page, focus stayed on the anchor the render had just detached, and a
 * screen reader was never told the page had changed. The installed app runs
 * `display: standalone`, so there is no browser chrome to compensate.
 *
 * @param {string} path
 */
function afterRender(path) {
  if (typeof window === 'undefined') return;

  if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);

  // Chrome-side title + live-region announcement. Imported lazily so the
  // router keeps no static dependency on the layout it renders into.
  import('./components/layout.js')
    .then((layout) => { if (layout.setHeaderRoute) layout.setHeaderRoute(path); })
    .catch(() => {});

  // Focus the new page's heading so keyboard and screen-reader users start at
  // the top of the content, not wherever the previous page left them.
  //
  // Deliberately NOT on the first render. There the jump buys nothing — the
  // document just loaded, focus already sits at the top, and no DOM swap
  // happened that a user could lose their place in. It is not invisible either:
  // with no user interaction yet in a fresh document, browsers resolve their
  // focus-visible heuristic in favour of showing the indicator, so the heading
  // renders with a focus ring around it on every hard reload. The same jump
  // during in-app navigation draws nothing, because the last input modality was
  // a pointer. Skipping the first render removes the artefact and keeps the
  // accessibility benefit exactly where it applies.
  if (!firstRenderDone) {
    firstRenderDone = true;
    return;
  }
  const target = outletEl.querySelector('h1') || outletEl;
  if (target && typeof target.focus === 'function') {
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
}

/** Re-run the current route without changing the URL (e.g. after a locale change). */
export function rerender() {
  return render();
}

export function handleUnauthorized(reason) {
  const key = REASON_TO_I18N_KEY[reason] || 'auth.toast.sessionExpired';
  // Redirect immediately (no delayed half-authenticated flash); the login
  // page reads the stored reason and shows it as an inline notice.
  try { sessionStorage.setItem(AUTH_NOTICE_STORAGE_KEY, key); } catch { /* private mode */ }
  clear();
  if (parseHash() !== '/login') go('/login');
}

export function start() {
  // The app resets scroll itself on every route change; letting the browser
  // re-apply a remembered offset would fight that.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  window.addEventListener('hashchange', render);
  setUnauthorizedHandler(handleUnauthorized);
  render();
}
